/**
 * Every failing step of a refused reminder plan, not just the first.
 *
 * The create/reschedule endpoints used to stop at the first bad step and answer
 * `{ error: "steps[2]: sendAt must be in the future." }`, so a host with three
 * bad steps fixed them one refusal at a time. OrchestratorAPI now validates the
 * whole plan and ALSO answers `errors: [{ label, message }, ...]`, one entry per
 * failing step, keeping `error` (the first one) for older clients. Studio lists
 * every entry when `errors` is present and falls back to the single message when
 * it is not (an API that predates it).
 *
 * The shape (OrchestratorAPI #417), one entry per failing step in step order:
 *
 *     { "error": "steps[0]: ...", "stepIndex": 0,
 *       "errors": [ { "label": "rsvp_chase", "stepIndex": 0, "message": "sendAt must be in the future." } ] }
 *
 * `label` is the step's `sequenceStep` (null when it has none), `stepIndex` is
 * its 0-based position in the request, and `message` carries no "steps[i]:"
 * prefix. Plan-level failures (not published, step count, anchor date, missing
 * id) stay a single `{ error }` with no `errors`. Validation runs in phases, so
 * fixing these can surface new ones on the next attempt: show whatever comes back.
 *
 * Studio places an entry by its `stepIndex` first (a position cannot be ambiguous;
 * a label can repeat) and, when there is none, by its label (a step key, a display
 * name, or the "steps[i]" position the older text used); anything it cannot place
 * is shown as written.
 *
 * Pure and dependency-free (the caller supplies the display-name lookup), so
 * lib/reminderPlan.ts can use it without a cycle.
 */

export interface PlanErrorEntry {
  /** What the server called the step, or null when it named none. */
  label: string | null;
  /** 0-based position of the step in the request, when the server said. */
  stepIndex?: number;
  message: string;
}

const STEP_POSITION = /^steps\[(\d+)\]:?\s*/;

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/**
 * The entries the API returned, in order. Empty when the body has no usable
 * `errors` array (the caller then falls back to the single `error` message).
 */
export function readPlanErrorEntries(error: unknown): PlanErrorEntry[] {
  const raw = (error as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors;
  if (!Array.isArray(raw)) return [];
  const entries: PlanErrorEntry[] = [];
  for (const item of raw) {
    if (typeof item === 'string') {
      if (text(item)) entries.push({ label: null, message: text(item) });
      continue;
    }
    const message = text((item as { message?: unknown } | null)?.message);
    if (!message) continue;
    const index = (item as { stepIndex?: unknown }).stepIndex;
    entries.push({
      label: text((item as { label?: unknown }).label) || null,
      ...(typeof index === 'number' && Number.isInteger(index) && index >= 0 ? { stepIndex: index } : {}),
      message,
    });
  }
  return entries;
}

/**
 * Which of the plan's steps an entry belongs to, as the step KEY, or null when
 * it cannot be told. `stepKeys` is the keys in request order (the order the
 * server's `steps[i]` counts in); `nameOf` turns a key into the label a host reads.
 */
export function entryStepKey(
  entry: Pick<PlanErrorEntry, 'label' | 'stepIndex'>,
  stepKeys: readonly string[],
  nameOf: (key: string) => string,
): string | null {
  // The position comes first: it is unambiguous, whereas a label can repeat (two "custom" steps) or be a display name.
  if (entry.stepIndex !== undefined) {
    const positional = stepKeys[entry.stepIndex];
    if (positional !== undefined) return positional;
  }
  const { label } = entry;
  if (label) {
    const position = STEP_POSITION.exec(label);
    if (position) return stepKeys[Number(position[1])] ?? null;
    if (stepKeys.includes(label)) return label;
    const wanted = label.toLowerCase();
    const named = stepKeys.find((key) => nameOf(key).toLowerCase() === wanted);
    if (named) return named;
  }
  return null;
}

/** One line a host reads: "Day before: sendAt must be in the future." */
export function entryLine(
  entry: PlanErrorEntry,
  stepKeys: readonly string[],
  nameOf: (key: string) => string,
): string {
  const key = entryStepKey(entry, stepKeys, nameOf);
  const message = entry.message.replace(STEP_POSITION, '');
  if (key) return `${nameOf(key)}: ${message}`;
  return entry.label && !STEP_POSITION.test(entry.label) ? `${entry.label}: ${message}` : message;
}

/** The entries grouped by the step they belong to; entries that name no known step are left out. */
export function planErrorsByStep(
  error: unknown,
  stepKeys: readonly string[],
  nameOf: (key: string) => string,
): Record<string, string[]> {
  const grouped: Record<string, string[]> = {};
  let entries = readPlanErrorEntries(error);
  if (entries.length === 0) {
    // An API without `errors`: its one message may still name the step ("steps[2]: ...").
    const legacy = text((error as { response?: { data?: { error?: unknown } } })?.response?.data?.error);
    const stepIndex = (error as { response?: { data?: { stepIndex?: unknown } } })?.response?.data?.stepIndex;
    if (STEP_POSITION.test(legacy)) entries = [{ label: STEP_POSITION.exec(legacy)![0].trim().replace(/:$/, ''), message: legacy }];
    else if (typeof stepIndex === 'number' && legacy) entries = [{ label: null, stepIndex, message: legacy }];
  }
  for (const entry of entries) {
    const key = entryStepKey(entry, stepKeys, nameOf);
    if (!key) continue;
    (grouped[key] ??= []).push(entry.message.replace(STEP_POSITION, ''));
  }
  return grouped;
}
